/**
 * Labstep
 *
 * @module components/Metadata/Action/Create/Modal/Wizard
 * @desc Wizard modal for creating entity_relation metadata
 */

import { MetadataActionCreateModalWizardStepAdditionalSettings } from 'labstep-web/components/Metadata/Action/Create/Modal/Wizard/StepAdditionalSettings';
import { MetadataActionCreateModalWizardStepAddLookupFields } from 'labstep-web/components/Metadata/Action/Create/Modal/Wizard/StepAddLookupFields';
import { MetadataActionCreateModalWizardStepFieldName } from 'labstep-web/components/Metadata/Action/Create/Modal/Wizard/StepFieldName';
import { MetadataActionCreateModalWizardStepRelationType } from 'labstep-web/components/Metadata/Action/Create/Modal/Wizard/StepRelationType';
import { MetadataActionCreateModalWizardStepRestrictCategory } from 'labstep-web/components/Metadata/Action/Create/Modal/Wizard/StepRestrictCategory';
import type { IValues } from 'labstep-web/core/Form/Reusable/types';
import { ModalWizard } from 'labstep-web/core/Modal/Wizard';
import { useActiveGroup } from 'labstep-web/hooks/activeGroup';
import { useModalRoute } from 'labstep-web/hooks/modalRoute';
import type { ModalRouteStateType } from 'labstep-web/hooks/modalRoute/types';
import type { APICallOptions, APIResponse } from 'labstep-web/models';
import { EntityRelationType } from 'labstep-web/models/field-definition.model';
import type { Metadata } from 'labstep-web/models/metadata';
import {
  type MetadataParent,
  MetadataType,
} from 'labstep-web/models/metadata/types';
import { selectEntity } from 'labstep-web/state/selectors/entity';
import type React from 'react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

export const METADATA_ENTITY_RELATION_WIZARD_ROUTE_ID =
  'metadata-entity-relation-wizard';

export const MetadataActionCreateModalWizard: React.FC = () => {
  const { activeGroup } = useActiveGroup();
  const { toggleModal } = useModalRoute(
    METADATA_ENTITY_RELATION_WIZARD_ROUTE_ID,
  );
  const { state: locationState } = useLocation<ModalRouteStateType>();
  const [entityId, setEntityId] = useState<string | null>(null);
  const [entityName, setEntityName] = useState<string | null>(null);

  useEffect(() => {
    if (locationState?.entityId && locationState?.entityName) {
      setEntityId(locationState.entityId);
      setEntityName(locationState.entityName);
    }
  }, [locationState?.entityId, locationState?.entityName]);

  const entity = useSelector((state) =>
    entityId && entityName
      ? selectEntity(state, entityName, entityId)
      : null,
  ) as MetadataParent | null;

  const [formValues, setFormValues] = useState<IValues>({
    type: {
      value: MetadataType.entity_relation,
      label: 'Entity Relation',
    },
    entity_relation_type: EntityRelationType.one_way,
    current_entity: entity,
  });

  const [selectedLookupFields, setSelectedLookupFields] = useState<
    Metadata[]
  >([]);
  const [createdMetadata, setCreatedMetadata] =
    useState<Metadata | null>(null);
  const createLookupFieldsRef = useRef<any>(null);

  useEffect(() => {
    if (entity) {
      const updates: IValues = { current_entity: entity };

      // Pre-populate inverse_relation with current entity name for non-template entities
      const isTemplate = (entity as any)?.is_template ?? false;
      if (!isTemplate && (entity as any)?.name) {
        updates.inverse_relation = (entity as any).name;
      }

      setFormValues((prev) => ({ ...prev, ...updates }));
    }
  }, [entity]);

  const isTemplate = (entity as any)?.is_template ?? false;

  // Determine if we should show the lookup fields step
  const targetTemplateId =
    formValues.entity_relation_target_entity_filter_template_id as
      | string
      | undefined;
  const currentEntityId = entity?.id || (entity as any)?.guid;
  const shouldShowLookupFieldsStep =
    isTemplate &&
    targetTemplateId &&
    targetTemplateId !== currentEntityId;

  // If relation was created and we should show lookup fields, show them
  const showingLookupFieldsStep =
    createdMetadata && shouldShowLookupFieldsStep;

  const stepTitles = isTemplate
    ? shouldShowLookupFieldsStep
      ? [
          'Select Category',
          'Relation Type',
          'Field Name',
          'Additional Settings',
          'Add Lookup Fields',
        ]
      : [
          'Select Category',
          'Relation Type',
          'Field Name',
          'Additional Settings',
        ]
    : ['Relation Type', 'Field Name', 'Additional Settings'];

  const handleFormChange = useCallback((values: IValues) => {
    setFormValues((prev) => ({ ...prev, ...values }));
  }, []);

  const handleLookupFieldToggle = useCallback((field: Metadata) => {
    setSelectedLookupFields((prev) =>
      prev.find((f) => f.id === field.id)
        ? prev.filter((f) => f.id !== field.id)
        : [...prev, field],
    );
  }, []);

  const body = useMemo(
    () => ({
      group_id: activeGroup?.guid,
      type: MetadataType.entity_relation,
    }),
    [activeGroup],
  );

  const reset = useCallback(() => {
    setFormValues({
      type: {
        value: MetadataType.entity_relation,
        label: 'Entity Relation',
      },
      entity_relation_type: EntityRelationType.one_way,
      current_entity: entity,
    });
    setSelectedLookupFields([]);
    setCreatedMetadata(null);
    setEntityId(null);
    setEntityName(null);
  }, [entity]);

  const handleMainFieldCreated = useCallback(
    ({ response }: { response: APIResponse }) => {
      if (shouldShowLookupFieldsStep) {
        const metadata =
          response.entities.metadata[response.result as number];
        setCreatedMetadata(metadata as Metadata);
      } else {
        // No lookup fields to add, close wizard
        reset();
        toggleModal();
      }
    },
    [shouldShowLookupFieldsStep, reset, toggleModal],
  );

  const handleLookupFieldsFinish = useCallback((create: any) => {
    createLookupFieldsRef.current = create;
  }, []);

  const handleLookupFieldsContinue = useCallback(() => {
    // field_definition can be either an object with .id or a string (the ID itself after normalization)
    const fieldDefinitionId =
      typeof createdMetadata?.field_definition === 'string'
        ? createdMetadata.field_definition
        : createdMetadata?.field_definition?.id;

    if (
      selectedLookupFields.length > 0 &&
      fieldDefinitionId &&
      createLookupFieldsRef.current
    ) {
      const metadataPayloads = selectedLookupFields.map((field) => ({
        label: field.label,
        field_definition: {
          name: field.label,
          data_type: field.type,
          lookup_relation_field_definition_id: fieldDefinitionId,
        },
      }));

      createLookupFieldsRef.current(metadataPayloads, {
        toast: true,
        onSuccess: () => {
          reset();
          toggleModal();
        },
      });
    } else {
      // No fields selected or missing field_definition, just close
      reset();
      toggleModal();
    }
  }, [selectedLookupFields, createdMetadata, reset, toggleModal]);

  // Calculate step indices based on whether isTemplate
  const step1Index = 0;
  const step2Index = isTemplate ? 1 : 0;
  const step3Index = isTemplate ? 2 : 1;
  const step4Index = isTemplate ? 3 : 2;
  const step5Index = isTemplate ? 4 : 3;

  if (!entity) {
    return null;
  }

  return (
    <ModalWizard
      mode="mini"
      routeId={METADATA_ENTITY_RELATION_WIZARD_ROUTE_ID}
      title={`Add Relation Field`}
      onClose={reset}
      steps={stepTitles}
      onFinish={reset}
    >
      {/* Step 1: Restrict resources to specific category (only if isTemplate) */}
      {isTemplate && (
        <ModalWizard.Step
          index={step1Index}
          description="Choose which category of resources this field will link to"
          hideHeader
        >
          <MetadataActionCreateModalWizardStepRestrictCategory
            formValues={formValues}
            onFormChange={handleFormChange}
          />
        </ModalWizard.Step>
      )}

      {/* Step 2: Relation Type */}
      <ModalWizard.Step
        index={step2Index}
        hideHeader
        description="What type of relation would you like to create?"
      >
        <MetadataActionCreateModalWizardStepRelationType
          formValues={formValues}
          onFormChange={handleFormChange}
          isTemplate={isTemplate}
        />
      </ModalWizard.Step>

      {/* Step 3: Field Name and Inverse Relation */}
      <ModalWizard.Step
        index={step3Index}
        description="Customize the relation field name(s)"
        hideHeader
      >
        <MetadataActionCreateModalWizardStepFieldName
          formValues={formValues}
          onFormChange={handleFormChange}
        />
      </ModalWizard.Step>

      {/* Step 4: Additional Settings */}
      <MetadataActionCreateModalWizardStepAdditionalSettings
        entity={entity}
        isTemplate={isTemplate}
        formValues={formValues}
        body={body}
        onSuccess={
          handleMainFieldCreated as APICallOptions['onSuccess']
        }
        stepIndex={step4Index}
      />

      {/* Step 5: Add Lookup Fields (conditional - shown after relation is created) */}
      {showingLookupFieldsStep && (
        <ModalWizard.Step
          index={step5Index}
          description="Do you want to add any metadata fields about the related resource as lookup fields?"
          hideHeader
          onContinue={handleLookupFieldsContinue}
          continueLabel={
            selectedLookupFields.length > 0
              ? `Add ${selectedLookupFields.length} Selected Fields`
              : 'Skip'
          }
          hideBackButton
        >
          <MetadataActionCreateModalWizardStepAddLookupFields
            targetTemplateId={targetTemplateId}
            selectedFields={selectedLookupFields}
            onFieldToggle={handleLookupFieldToggle}
            createdMetadata={createdMetadata}
            entity={entity}
            onFinish={handleLookupFieldsFinish}
          />
        </ModalWizard.Step>
      )}
    </ModalWizard>
  );
};
